import { MapPin, Mail, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Contact = () => {
  const { t } = useLanguage();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const emails = [
    { label: 'General', value: 'office-loz@artifex-systems.com' },
    { label: 'Human Resources', value: 'hr-loz@artifex-systems.com' },
    { label: 'Logistics', value: 'logistika-loz@artifex-systems.com' },
  ];

  const address = [
    'Artifex Systems Slovakia s.r.o.',
    'Automobilový priemyselný park',
    'Lozorno 1006, 900 31 Lozorno',
    'Slovakia',
  ];

  return (
    <section id="contact" ref={ref} className="section-pad bg-background">
      <div className="container-tight">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <h2 className="font-display font-bold text-primary" style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)', lineHeight: 0.98, letterSpacing: '-0.03em' }}>
              {t('contact.title')}
            </h2>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left: details */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5 space-y-10"
          >
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Mail className="w-4 h-4 text-secondary" />
                <span className="font-display text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  {t('contact.email')}
                </span>
              </div>
              <ul className="space-y-4">
                {emails.map((e) => (
                  <li key={e.value} className="flex items-baseline justify-between pb-4 border-b border-[hsl(var(--hairline))]">
                    <span className="text-muted-foreground text-sm">{e.label}</span>
                    <a
                      href={`mailto:${e.value}`}
                      className="group font-display font-semibold text-primary hover:text-secondary transition-colors inline-flex items-center gap-1.5"
                    >
                      {e.value}
                      <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-4 h-4 text-secondary" />
                <span className="font-display text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  {t('contact.address')}
                </span>
              </div>
              <address className="not-italic font-display text-lg md:text-xl text-primary leading-relaxed">
                {address.map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </address>
            </div>
          </motion.div>

          {/* Right: map */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-7"
          >
            <div className="relative rounded-xl overflow-hidden border border-[hsl(var(--hairline))] h-full min-h-[440px] bg-muted">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d2651.533918881033!2d17.024996312031185!3d48.35026333621063!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1ssk!2ssk"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '440px', filter: 'grayscale(0.2) contrast(1.02)' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Artifex Systems Slovakia — Lozorno"
              />
              <div className="absolute left-4 right-4 bottom-4 md:right-auto md:max-w-sm rounded-lg bg-white/95 backdrop-blur-sm border border-[hsl(var(--hairline))] shadow-[var(--shadow-card)] p-4">
                <p className="font-display font-semibold text-primary">Artifex Systems Slovakia s.r.o.</p>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                  Automobilový priemyselný park<br />
                  Lozorno 1006, 900 31 Lozorno
                </p>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Artifex+Systems+Slovakia+s.r.o.+Lozorno+1006"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group mt-3 inline-flex items-center gap-1.5 font-display text-sm font-semibold text-primary hover:text-secondary transition-colors"
                >
                  Otvoriť v Mapách
                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
